import { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_MENU_COLOR = '#064E3B';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [currentSeasonName, setCurrentSeasonName] = useState(null);
  // Combine all splash state into a single object to ensure atomic updates
  const [splashState, setSplashState] = useState({
    show: false,
    color: null,
    badge: null,
    displayName: null
  });
  const lastFetchedSeasonId = useRef(null);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Fetch season name when current_season_id changes
  useEffect(() => {
    const fetchSeasonName = async () => {
      const seasonId = user?.current_season_id;
      
      if (seasonId && seasonId !== lastFetchedSeasonId.current) {
        try {
          const response = await axios.get(`${API}/seasons/${seasonId}`);
          setCurrentSeasonName(response.data.name);
          lastFetchedSeasonId.current = seasonId;
        } catch (error) {
          console.error('Failed to fetch season name:', error);
          setCurrentSeasonName(null);
          lastFetchedSeasonId.current = null;
        }
      } else if (!seasonId && lastFetchedSeasonId.current !== null) {
        setCurrentSeasonName(null);
        lastFetchedSeasonId.current = null;
      }
    };
    
    if (user) {
      fetchSeasonName();
    }
  }, [user?.current_season_id]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      setUser(userData);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(`${API}/auth/login`, { email, password, remember_me: rememberMe });
    const { access_token, user: userData } = response.data;
    
    // For super admins, try to use branding splash color
    let splashBg = userData.menu_color || DEFAULT_MENU_COLOR;
    if (userData.role === 'super_admin') {
      try {
        const brandingRes = await axios.get(`${API}/landing/content`);
        const brandingSplash = brandingRes.data?.branding?.splashColor;
        if (brandingSplash) splashBg = brandingSplash;
      } catch {}
    }
    
    // Show splash with color in a single atomic update FIRST
    setSplashState({
      show: true,
      color: splashBg,
      badge: userData.club_badge || null,
      displayName: userData.role === 'player' ? userData.name : (userData.display_name || userData.name || null)
    });
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (name, email, password) => {
    // If the visitor arrived via a public coach's share link, `LandingPage`
    // stashes the referrer's public slug in localStorage. Include it in the
    // signup payload so the referring coach gets credit on their dashboard.
    let referred_by_slug = null;
    try { referred_by_slug = localStorage.getItem('gc-referral-slug'); }
    catch { /* private mode — carry on without a ref */ }
    const payload = { name, email, password };
    if (referred_by_slug) payload.referred_by_slug = referred_by_slug;
    const response = await axios.post(`${API}/auth/register`, payload);
    // Clear the referral after a successful signup — a signup only counts once.
    if (referred_by_slug) {
      try { localStorage.removeItem('gc-referral-slug'); } catch { /* ignore */ }
    }
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setCurrentSeasonName(null);
    setSplashState({ show: false, color: null, badge: null, displayName: null });
    lastFetchedSeasonId.current = null;
  };

  const hideSplash = () => {
    setSplashState(prev => ({ ...prev, show: false }));
  };
  
  // Derived values for easier access
  const showSplash = splashState.show;
  const splashColor = splashState.color;
  const splashBadge = splashState.badge;
  const splashDisplayName = splashState.displayName;

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  // Role helpers
  const isSuperAdmin = user?.role === 'super_admin';
  const isCoach = user?.role === 'coach' || user?.role === 'admin'; // Include 'admin' for backward compatibility
  const isAdmin = isSuperAdmin || isCoach; // Can manage their team
  const isPlayer = user?.role === 'player';
  const canEdit = isAdmin; // Only admins/coaches can edit their team data
  const needsOnboarding = isCoach && !user?.is_onboarded;
  
  // Subscription status
  const subStatus = user?.subscription_status;
  const hasActiveSubscription = isSuperAdmin || isPlayer || 
    subStatus === 'active' || subStatus === 'trialing' || subStatus === 'cancelling' ||
    (user?.plan && user.plan !== 'free');
  const needsSubscription = isCoach && user?.is_onboarded && !hasActiveSubscription;

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      updateUser, 
      fetchUser, 
      currentSeasonName, 
      showSplash, 
      hideSplash, 
      splashColor, 
      splashBadge, 
      splashDisplayName,
      isSuperAdmin,
      isCoach,
      isAdmin,
      isPlayer,
      canEdit,
      needsOnboarding,
      hasActiveSubscription,
      needsSubscription
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
